import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Envelope } from '../../interfaces/envelope';
import { EventTypes } from '../../interfaces/events';
import { Trend } from '../../interfaces/trends';
import { trendsService } from '../services/trends.service';
import { EventsParam } from '../../api/events';

type TrendObjects = {
	dt_ambient_temperature_C?: Trend[];
	dt_relative_humidity_pct?: Trend[];
};

interface TrendsState {
	trends: {
		[id: number]: TrendObjects;
	};
	isLoading: boolean;
}

const initialState = {
	trends: [],
	isLoading: false,
} as TrendsState;

export const fetchTrends = createAsyncThunk<
	{ trends: Trend[]; devices: string[]; types: EventTypes[] },
	{ param: EventsParam },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('trends/fetchTrends', async ({ param }, { rejectWithValue }) => {
	try {
		const trends = await trendsService.fetchTrends(param);
		return {
			trends: trends,
			devices: param.fk_devices,
			types: param.fk_event_types as EventTypes[],
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

const trendsSlice = createSlice({
	name: 'trends',
	initialState,
	reducers: {
		resetTrends: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchTrends.fulfilled, (state, action) => {
			for (const device of action.payload.devices) {
				const deviceTrends: Trend[] = action.payload.trends.filter(
					(event: Trend) => event.fk_device === Number(device)
				);
				let trendObjects: TrendObjects = {};
				for (const eventType of action.payload.types) {
					trendObjects[eventType] = deviceTrends.filter(
						(event) => event.fk_event_type === eventType
					);
				}

				state.trends = {
					...state.trends,
					[Number(device)]: { ...state.trends[Number(device)], ...trendObjects },
				};
			}
			state.isLoading = false;
		});
		builder.addCase(fetchTrends.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchTrends.rejected, (state, action) => {
			state.trends = [];

			state.isLoading = false;
		});
	},
});

export const { resetTrends } = trendsSlice.actions;

export default trendsSlice;
